// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// UI komponente
import { NoResults } from "ui/components/NoResults";
import { Loader } from "ui/components/Loader";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

import { Dialog } from "ui/components/Dialog";
import { FleetVehicleContractSaleRow, FleetVehicleContractSaleSearchFormValues } from "services/api/fleet/vehicles/contracts/types";
import { VehicleContractSaleSearchPageCmp, VehicleContractSaleSearchPageProps } from "../types";
import { deleteFleetVehicleContractSaleAPI, getFleetVehicleContractSalesAPI } from "services/api/fleet/vehicles/contracts/api";

import VehicleContractSaleEditCard from "../../../VehicleContractSaleEditCard/VehicleContractSaleEditCard";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import VehicleContractSaleSearchFilters from "../VehicleContractSaleSearchFilters/VehicleContractSaleSearchFilters";
import VehicleContractSaleSearchResults from "../VehicleContractSaleSearchResults/VehicleContractSaleSearchResults";

const VehicleContractSaleSearchPage: VehicleContractSaleSearchPageCmp = (
    props: VehicleContractSaleSearchPageProps
) => {
    const { id } = useParams();

    const { t } = useTranslate();

    const navigate = useNavigate();

    const [searchResults, setSearchResults] = useState<
        DataWithPagination<FleetVehicleContractSaleRow>
    >(initialDataWithPagination);

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [contractId, setContractId] = useState<string>("");
    const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<FleetVehicleContractSaleSearchFormValues>({
        search: null, contractSaleStatusId: null, contractDateFrom: null, contractDateTo: null,
        sellingPriceMin: null, sellingPriceMax: null, contactClientId: null,
    });


    useEffect(() => {
        if (id) {
            fetchSearchResult(id, searchResults.pagination, filters);
        }
    }, [id]);

    const fetchSearchResult = async (id: string, pagination: Pagination, filterValues?: FleetVehicleContractSaleSearchFormValues) => {
        setIsLoading(true);
        try {
            const f = filterValues ?? filters;
            const results = await getFleetVehicleContractSalesAPI(pagination, {
                vehicleId: id,
                contractSaleStatusId: f.contractSaleStatusId ?? undefined,
                search: f.search ?? undefined,
                contractDateFrom: f.contractDateFrom,
                contractDateTo: f.contractDateTo,
                sellingPriceMin: f.sellingPriceMin,
                sellingPriceMax: f.sellingPriceMax,
                contactClientId: f.contactClientId ?? undefined,
            });
            setSearchResults(results);
        } catch {
            setSearchResults(initialDataWithPagination);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    };

    const handleRowClick = useCallback(async (id: string) => {
        setContractId(id);
        setShowAddNewModal(true);
    }, []);

    const handleAddNew = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setContractId("");
            setShowAddNewModal(true);
        },
        []
    );

    const handleEditSuccess = useCallback(() => {
        if (id) fetchSearchResult(id, searchResults.pagination);

        setContractId("");
        setShowAddNewModal(false);
    }, [id, searchResults.pagination]);

    const handleSearchChange = useCallback((formValues: FleetVehicleContractSaleSearchFormValues) => {
        setFilters(formValues);
        if (id) fetchSearchResult(id, searchResults.pagination, formValues);
    }, [id, searchResults.pagination]);

    const handleSelectedChange = useSelectedItemsChanges(
        selectedRows,
        setSelectedRows
    );

    const handlePaginationChange = (newPagination: Pagination) => {
        if (id) fetchSearchResult(id, newPagination);
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, contractSaleId: string) => {
        event.stopPropagation();
        event.preventDefault();

        setContractId(contractSaleId);
        setOpenDialog(true);
    }

    const handleContractDeleteDialogConfirm = useCallback(async () => {
        if (!contractId || !id) return;

        try {
            const deleted = await deleteFleetVehicleContractSaleAPI(id, contractId, t);

            if (deleted && id) {
                await fetchSearchResult(id, searchResults.pagination);
            }

            setOpenDialog(false);
            setContractId("");
        } catch (err) {
            console.error("Greška prilikom brisanja ugovora:", err);
        }
    }, [contractId, t]);

    const handleContractDeleteDialogClose = useCallback(() => {
        setContractId("");
        setOpenDialog(false);
    }, []);

    const handleContactClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, contactId: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                ContactRoutes.ContactDetailPageRouter,
                {
                    id: contactId,
                }
            )
        );
    }

    if (!id) return <></>;

    return (
        <>
            <div className="px-0 py-6 mx-[-1px] mt-[-1px] xl:px-6 border ">
                <div className="text-xl font-bold text-slate-800">
                    Prodajni ugovor
                </div>
                <VehicleContractSaleSearchFilters
                    onAddNew={handleAddNew}
                    onSearchChange={handleSearchChange}
                />
                {isLoading && searchResults.data.length === 0 ? (
                    <div className="relative min-h-[200px]">
                        <Loader isLoading={true}>{null}</Loader>
                    </div>
                ) : !isLoading && searchResults.pagination.totalPages === 0 ? (
                    <NoResults message="Unesite ugovor klikom na 'Dodaj ugovor'" />
                ) : (
                    <VehicleContractSaleSearchResults
                        searchResults={searchResults}
                        selectedRows={selectedRows}
                        isLoading={isLoading}
                        onRowClick={handleRowClick}
                        onPaginationChange={handlePaginationChange}
                        onSelectedChange={handleSelectedChange}
                        onContactClick={handleContactClick}
                        onDeleteClick={handleDeleteClick}
                    />
                )}
            </div>
            <Dialog
                open={openDialog}
                title={t(
                    "admin.fleet.vehicle.contract.dialog.delete.title"
                )}
                message={t(
                    "admin.fleet.vehicle.contract.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.fleet.vehicle.contract.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.fleet.vehicle.contract.dialog.delete.canceltext"
                )}
                onConfirm={handleContractDeleteDialogConfirm}
                onCancel={handleContractDeleteDialogClose}
            />
            <VehicleContractSaleEditCard
                show={showAddNewModal}
                vehicleId={id}
                contractId={contractId}
                onSuccess={handleEditSuccess}
                onClose={handleEditSuccess}
            />
        </>
    );
};

VehicleContractSaleSearchPage.route =
    FleetVehicleRoutes.FleetVehicleContractSearchPageRouter;

export default VehicleContractSaleSearchPage;
